"use client";

import { Api } from "@/app/api";
import { SignUpDTO } from "@/types";
import { signOut } from "next-auth/react";

type SignUpResponse = {
	id_token: string;
	token_type: string;
	expires_in: string;
	user: unknown;
};

// Helper function to recursively remove disallowed properties
const cleanSignupBody = (body: any): SignUpDTO => {
	if (!body || typeof body !== 'object') return body;
	
	const disallowedProperties = ['isActive', 'role', 'id', '_id', 'plan', 'onboardingStages', 'affiliateInfo', 'isConsentAccepted', 'createdAt', 'onlineStatus', 'affiliatedBy', 'bio'];
	
	// Use JSON serialization/deserialization to create a completely clean object
	// This removes any getters, setters, or prototype properties
	let cleaned: any;
	try {
		cleaned = JSON.parse(JSON.stringify(body));
	} catch (e) {
		// Fallback to manual cleaning if JSON serialization fails
		cleaned = {};
		const keys = Object.keys(body);
		for (const key of keys) {
			if (!disallowedProperties.includes(key)) {
				cleaned[key] = body[key];
			}
		}
	}
	
	// Remove disallowed properties from the cleaned object
	disallowedProperties.forEach(prop => {
		if (Object.prototype.hasOwnProperty.call(cleaned, prop)) {
			console.warn(`[signUp] Removing disallowed property '${prop}' from signup request`);
			delete cleaned[prop];
		}
	});
	
	// Recursively clean nested objects
	for (const key in cleaned) {
		if (cleaned[key] !== null && typeof cleaned[key] === 'object' && !Array.isArray(cleaned[key]) && !(cleaned[key] instanceof Date)) {
			cleaned[key] = cleanSignupBody(cleaned[key]);
		}
	}
	
	return cleaned as SignUpDTO;
};

export const authInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		signUp: builder.mutation<SignUpResponse, SignUpDTO>({
			query: (body) => {
				// Clean the body to remove any disallowed properties
				const cleanedBody = cleanSignupBody(body);
				
				// Create a fresh object by JSON serialization/deserialization to ensure no hidden properties
				// This is the most reliable way to ensure only the properties we want are sent
				let serializedBody: any;
				try {
					serializedBody = JSON.parse(JSON.stringify(cleanedBody));
				} catch (e) {
					// If serialization fails, use cleaned body directly
					serializedBody = cleanedBody;
				}
				
				// Final check: remove any disallowed properties from the serialized body using hasOwnProperty
				const disallowedProperties = ['isActive', 'role', 'id', '_id', 'plan', 'onboardingStages', 'affiliateInfo', 'isConsentAccepted', 'createdAt', 'onlineStatus', 'affiliatedBy', 'bio'];
				disallowedProperties.forEach(prop => {
					if (Object.prototype.hasOwnProperty.call(serializedBody, prop)) {
						console.warn(`[signUp] Removing '${prop}' from serialized body`);
						delete serializedBody[prop];
					}
				});
				
				// Create a final clean object with only allowed properties
				const finalBody: any = {};
				const allowedKeys = Object.keys(serializedBody).filter(key => !disallowedProperties.includes(key));
				allowedKeys.forEach(key => {
					finalBody[key] = serializedBody[key];
				});
				
				// Log in development to help debug
				if (process.env.NODE_ENV === 'development') {
					console.log('[signUp] Original body:', JSON.stringify(body, null, 2));
					console.log('[signUp] Cleaned body:', JSON.stringify(cleanedBody, null, 2));
					console.log('[signUp] Final serialized body:', JSON.stringify(finalBody, null, 2));
					console.log('[signUp] Checking for isActive in final body:', Object.prototype.hasOwnProperty.call(finalBody, 'isActive'));
					console.log('[signUp] Final body keys:', Object.keys(finalBody));
					console.log('[signUp] Final body stringified:', JSON.stringify(finalBody));
				}
				
				// Ensure the body is properly serialized as JSON string to avoid any property injection
				// This ensures RTK Query doesn't add any default properties
				return {
					url: "auth/signup",
					method: "POST",
					body: finalBody,
					headers: {
						"Content-Type": "application/json",
					},
				};
			},
		}),
		deleteAccount: builder.mutation<void, void>({
			query: (body) => ({
				url: "auth/delete-account-request",
				method: "POST",
			}),
		}),
		otpReq: builder.mutation<void, void>({
			query: () => ({
				url: "auth/otp-req",
				method: "POST",
				body: {},
			}),
		}),
		otpValidate: builder.mutation<void, string>({
			query: (code) => ({
				url: "auth/otp-validate",
				method: "POST",
				body: {
					code: code,
				},
			}),
		}),
		/**
		 * Forgot password is different from reset password since latter requires the user to have a valid session.
		 */
		forgotPassword: builder.mutation<{ message: string }, { email: string; captchaToken?: string }>({
			query: ({ email, captchaToken }) => ({
				url: "auth/forgot-password",
				body: { email, captchaToken },
				method: "POST",
				headers: { "Content-Type": "application/json" },
			}),
		}),
		verifyForgotPasswordOtp: builder.mutation<
			{ message: string; stepToken: string },
			{ email: string; code: string }
		>({
			query: (body) => ({
				url: "auth/forgot-password/verify-otp",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
		}),
		completeForgotPassword: builder.mutation<
			{ message: string },
			{
				email: string;
				stepToken: string;
				newPassword: string;
				confirmPassword: string;
			}
		>({
			query: (body) => ({
				url: "auth/forgot-password/complete",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
		}),
		resetPassword: builder.mutation<
			void,
			{
				oldPassword: string;
				newPassword: string;
				confirmPassword: string;
			}
		>({
			query: (body) => ({
				url: "auth/password-reset",
				method: "POST",
				body: body,
				headers: {
					"Content-Type": "application/json",
				},
			}),
		}),
	}),
});

export const {
	useSignUpMutation,
	useOtpReqMutation,
	useOtpValidateMutation,
	useResetPasswordMutation,
	useForgotPasswordMutation,
	useVerifyForgotPasswordOtpMutation,
	useCompleteForgotPasswordMutation,
	useDeleteAccountMutation,
} = authInjection;
